import type { EnemyProfile } from "../api/types";
import { formatNumber, t } from "../i18n/i18n";
import table from "./enemyProfiles.json";

/** Profil du chef ENI (registre D18) : mode facultatif des Données initiales, éteint par défaut. Les effets sur le
 *  moteur viennent de la table unique du serveur (`server/generex_engine/data/enemy_profiles.json`), recopiée à
 *  l'identique dans `enemyProfiles.json` (l'essai `enemyProfile.test.ts` refuse toute différence) : le bloc
 *  d'explication des Données initiales ne peut pas s'écarter de ce que le moteur applique. */

export type ProfileTable = typeof table;
export const PROFILE_TABLE: ProfileTable = table;
export const PROFILES = table.order as EnemyProfile[];
export const DEFAULT_PROFILE = table.default as EnemyProfile;

type Phase = "T1" | "T2" | "T3";
const PHASES: Phase[] = ["T1", "T2", "T3"];

/** Nom et devise de chaque profil (cartes de choix). */
export function profileName(key: EnemyProfile): string {
  switch (key) {
    case "methodique":
      return t("Méthodique");
    case "audacieux":
      return t("Audacieux");
    case "prudent":
      return t("Prudent");
    case "rigide":
      return t("Rigide");
  }
}

export function profileMotto(key: EnemyProfile): string {
  switch (key) {
    case "methodique":
      return t("Applique sa doctrine");
    case "audacieux":
      return t("Prend des risques");
    case "prudent":
      return t("Économise ses forces");
    case "rigide":
      return t("Commandement centralisé");
  }
}

function weakness(key: EnemyProfile): string {
  switch (key) {
    case "methodique":
      return t("Prévisible : ses réactions s'anticipent.");
    case "audacieux":
      return t("S'étire : flancs et logistique exposés.");
    case "prudent":
      return t("Laisse l'initiative.");
    case "rigide":
      return t("Vulnérable à la neutralisation de ses PC et au rythme imposé.");
  }
}

/** Incidents que la DIREX propose d'abord (illustration des mots-clés de la table). */
function incidents(key: EnemyProfile): string {
  switch (key) {
    case "methodique":
      return t("Incidents attendus, au rythme du plan : reconnaissance, prise de contact, contre-attaque planifiée");
    case "audacieux":
      return t("Raid sur un PC, coupure d'un itinéraire logistique, infiltrations");
    case "prudent":
      return t("Harcèlement, feux indirects, obstacles et mines plutôt que chocs frontaux");
    case "rigide":
      return t("Ordres ENI interceptés, flottement après la neutralisation d'un PC ENI");
  }
}

/** Facteur « ×1,3 » (« ×1.3 » en anglais). */
function factor(value: number): string {
  return `×${formatNumber(value, { minimumFractionDigits: 1, maximumFractionDigits: 2 })}`;
}

/** Durée « 30 min », « 1 h », « 1 h 30 », « 3 h ». */
export function duration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return t("{m} min", { m });
  if (m === 0) return t("{h} h", { h });
  return t("{h} h {m}", { h, m: String(m).padStart(2, "0") });
}

function percent(value: number): string {
  return formatNumber(Math.round(value * 100));
}

function capitalized(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function coaText(key: EnemyProfile): string {
  const coa = table.profiles[key].coa;
  const bonus = coa.bonus as Record<string, number[] | undefined>;
  if (coa.me1_from_danger)
    return t("Le mode d'action le plus dangereux devient le plus probable : débordement, infiltration ou raid dans la profondeur.");
  if ((bonus.defensive?.[0] ?? 0) > 0 || (bonus.harassment?.[0] ?? 0) > 0)
    return t("Modes d'action orientés vers la défensive : organisation du terrain, freinage prolongé, réserve gardée.");
  if (coa.rank_weight > table.profiles[DEFAULT_PROFILE].coa.rank_weight)
    return t("Modes d'action doctrinaux, choisis sur la doctrine ({doctrine} %) plus que sur le terrain ({terrain} %) : peu adaptés si la situation évolue.", {
      doctrine: percent(coa.rank_weight),
      terrain: percent(coa.geometry_weight),
    });
  return t("Le mode d'action le plus probable reste le schéma doctrinal ; le plus dangereux reste peu probable.");
}

function wargameText(key: EnemyProfile): string {
  const w = table.profiles[key].wargame;
  const tempo =
    w.tempo > 1
      ? t("tempo accéléré {factor}", { factor: factor(w.tempo) })
      : w.tempo < 1
        ? t("tempo ralenti {factor}", { factor: factor(w.tempo) })
        : t("tempo nominal {factor}", { factor: factor(w.tempo) });
  const delay = t("délai de réaction de {delay} après chaque imprévu", { delay: duration(w.reaction_delay_min) });
  const reserve =
    w.reserve_share < 1
      ? t("second échelon engagé en {phase}, pour {share} % de ses moyens", { phase: w.reserve_phase, share: percent(w.reserve_share) })
      : t("second échelon engagé en {phase}", { phase: w.reserve_phase });
  return `${capitalized(tempo)} · ${delay} · ${reserve}.`;
}

function direxText(key: EnemyProfile): string {
  const d = table.profiles[key].direx;
  const rhythm = d.rhythm as Record<Phase, number>;
  const changes = PHASES.filter((p) => rhythm[p] !== 1).map((p) =>
    t("{phase} {sign}{share} %", { phase: p, sign: rhythm[p] > 1 ? "+" : "−", share: percent(Math.abs(rhythm[p] - 1)) }),
  );
  const parts = [incidents(key)];
  if (changes.length > 0) {
    const more = PHASES.some((p) => rhythm[p] > 1);
    const fewer = PHASES.some((p) => rhythm[p] < 1);
    const label = more && !fewer ? t("plus d'incidents") : fewer && !more ? t("moins d'incidents") : t("rythme modifié");
    parts.push(t("{label} ({changes})", { label, changes: changes.join(", ") }));
  }
  if (d.earliness > 1) parts.push(t("injectés plus tôt dans chaque temps"));
  if (d.earliness < 1) parts.push(t("injectés plus tard dans chaque temps"));
  return `${parts.join(" · ")}.`;
}

export interface ProfileEffect {
  label: string;
  text: string;
}

/** Effets du profil sur le moteur, en clair, tirés de la table : le bloc « Effets » des Données initiales. */
export function profileEffects(key: EnemyProfile): ProfileEffect[] {
  return [
    { label: t("Modes d'action ENI"), text: coaText(key) },
    { label: t("Simuler les ME"), text: wargameText(key) },
    { label: t("DIREX"), text: direxText(key) },
    { label: t("Faiblesse à exploiter"), text: weakness(key) },
    {
      label: t("OPO et 3A"),
      text: t("Le § 1 de l'OPO décrit le comportement attendu du chef ENI ; la 3A ajoute le critère « L'unité a-t-elle décelé et exploité la faiblesse du chef ENI ? »."),
    },
  ];
}
